import React, { useEffect, useState, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, MessageCircle, ChevronRight, Edit, ArrowLeft, Info, CheckSquare, Clock, FileText, Plus, X, RefreshCw, Package, CheckCircle, XCircle, AlertCircle, ThumbsUp, Code, GitBranch, Search, Calendar, ChevronLeft, Copy, FilePenLine, Trash2, ClipboardList, Archive, Bell, Mail, Layers, Zap, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { CreateTask, TaskSetupScreen } from './CreateTask';
import { AutomationBuilder } from './AutomationBuilder';
import { DropdownMenu } from './DropdownMenu';
import { RunLog } from './RunLog';
import { Stepper } from './Stepper';
import { TableRowMenu } from './TableRowMenu';
import { WorkflowTypeSelection } from './WorkflowTypeSelection';
import { UsageChart } from './UsageChart';
import { LimitReachedModal } from './LimitReachedModal';
import { Tooltip } from './Tooltip';
export const Dashboard = () => {
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [isTaskSetupOpen, setIsTaskSetupOpen] = useState(false);
  const [isAutomationBuilderOpen, setIsAutomationBuilderOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [isNewTask, setIsNewTask] = useState(false);
  // Lift the automations state up to the Dashboard component
  const [taskAutomations, setTaskAutomations] = useState([]);
  const [isRunLogOpen, setIsRunLogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  // Add new state for workflow type selection modal
  const [isWorkflowTypeModalOpen, setIsWorkflowTypeModalOpen] = useState(false);
  // Add state for usage details modal
  const [isUsageDetailsOpen, setIsUsageDetailsOpen] = useState(false);
  // Add state for usage warning banner expanded state
  const [isWarningExpanded, setIsWarningExpanded] = useState(false);
  // Add state for limit reached modal
  const [isLimitReachedModalOpen, setIsLimitReachedModalOpen] = useState(false);
  // Add state for usage data
  const [usageData, setUsageData] = useState({
    used: 5,
    total: 5,
    resetDate: 'Feb 15',
    percentage: 100,
    daysLeft: 4
  });
  // Add state for limit reached
  const [isLimitReached, setIsLimitReached] = useState(true);
  // Simulate first-time limit reached modal on component mount
  useEffect(() => {
    if (isLimitReached) {
      setIsLimitReachedModalOpen(true);
    }
  }, []);
  const handleEditTask = task => {
    if (isLimitReached) {
      setIsLimitReachedModalOpen(true);
      return;
    }
    setCurrentTask(task);
    // Set initial automations based on the task
    if (task.title === 'Inventory Restock Management') {
      setTaskAutomations([{
        id: 1,
        name: 'Inventory Restock Notifier',
        description: 'Scheduled time → Delegate to AI Custom Agent',
        active: true
      }]);
    } else if (task.isNew) {
      setTaskAutomations([{
        id: 1,
        name: 'Scheduled Agent',
        description: 'Scheduled time → Delegate to AI Custom Agent',
        active: true
      }]);
    } else {
      // Default automations for other tasks
      setTaskAutomations([{
        id: 1,
        name: `${task.title} Automation`,
        description: 'Scheduled time → Delegate to AI Custom Agent',
        active: true
      }, {
        id: 2,
        name: 'Weekly Sales Summary',
        description: 'Scheduled time → Delegate to AI Custom Agent',
        active: true
      }, {
        id: 3,
        name: 'Feedback Analysis and Reporting',
        description: 'Option chosen on a form → Delegate to AI Custom Agent',
        active: true
      }]);
    }
    setIsEditTaskOpen(true);
    setIsNewTask(false);
  };
  const handleViewRunLog = workflow => {
    setSelectedWorkflow(workflow);
    setIsRunLogOpen(true);
  };
  const handleCloseRunLog = () => {
    setIsRunLogOpen(false);
    setSelectedWorkflow(null);
  };
  const handleEditAutomation = workflow => {
    // Remove the limit check to always allow editing
    // Check the workflow type and handle accordingly
    if (workflow.type === 'Builder') {
      // For Builder type, open AutomationBuilder with two delegate nodes
      sessionStorage.setItem('editingWorkflow', workflow.name);
      sessionStorage.setItem('isBuilderWorkflow', 'true');
      setIsAutomationBuilderOpen(true);
    } else {
      // For Basic type, edit using TaskSetupScreen instead of EditTask
      // Create task instructions based on workflow name
      let instructions = '';
      if (workflow.name === 'Inventory Restock Management') {
        instructions = `1. Analyze the inventory data to identify any items with stock levels below 20 units.
2. For each item identified, determine the corresponding supplier responsible for restocking that item.
3. Draft a personalized restock order email for each supplier, including the following details:
   - The item(s) that need restocking.
   - The current stock levels and the recommended restock quantity.
   - A polite request to confirm the order and provide an estimated delivery date.
4. Send the email to the respective supplier.`;
      } else if (workflow.name === 'Weekly Feedback Analysis and Summary') {
        instructions = `1. Collect all customer feedback submissions from the past 7 days.
2. Categorize feedback into predefined categories: Product Quality, Customer Service, Website Experience, Pricing, and Other.
3. Identify common themes and recurring issues within each category.
4. Calculate satisfaction scores and trend comparisons with previous weeks.
5. Generate a summary report with the following sections:
   - Key metrics (volume, sentiment, response rate)
   - Top 3 positive feedback themes
   - Top 3 areas for improvement
   - Urgent issues requiring immediate attention
6. Email the report to the management team every Monday at 9:00 AM.`;
      } else if (workflow.name === 'Negative Review Response with Coupon') {
        instructions = `1. Monitor incoming product reviews and identify any with a rating of 3 stars or lower.
2. For each negative review, analyze the specific complaint or issue mentioned.
3. Draft a personalized response that:
   - Thanks the customer for their feedback
   - Acknowledges the specific issue they experienced
   - Explains what steps will be taken to address the problem
   - Offers a one-time discount coupon code (15% off next purchase)
4. Submit the draft response for manager approval before posting.
5. Once approved, post the response and send the customer an email with their coupon code.
6. Flag the customer in the CRM system for follow-up in 14 days.`;
      } else {
        // Default instructions for other workflows
        instructions = `1. Review the incoming data from the triggering event.
2. Process the information according to business requirements.
3. Generate appropriate responses or actions based on the processed data.
4. Document the actions taken and results obtained.
5. Notify relevant stakeholders about the completed task.`;
      }
      const task = {
        title: workflow.name,
        instructions: instructions,
        isNew: false
      };
      setCurrentTask(task);
      // Set initial automations based on the task
      if (workflow.name === 'Inventory Restock Management') {
        setTaskAutomations([{
          id: 1,
          name: 'Inventory Restock Notifier',
          description: 'Scheduled time → Delegate to AI Custom Agent',
          active: true
        }]);
      } else {
        // Default automations for other tasks
        setTaskAutomations([{
          id: 1,
          name: `${workflow.name} Automation`,
          description: 'Scheduled time → Delegate to AI Custom Agent',
          active: true
        }, {
          id: 2,
          name: 'Weekly Sales Summary',
          description: 'Scheduled time → Delegate to AI Custom Agent',
          active: true
        }]);
      }
      // Open TaskSetupScreen instead of EditTask
      setIsTaskSetupOpen(true);
    }
  };
  const handleCreateTask = () => {
    // Remove limit check - always open workflow type modal
    setIsWorkflowTypeModalOpen(true);
  };
  const handleCloseWorkflowTypeModal = () => {
    setIsWorkflowTypeModalOpen(false);
  };
  const handleSelectWorkflowType = type => {
    setIsWorkflowTypeModalOpen(false);
    if (type === 'basic') {
      setIsCreateTaskOpen(true);
    }
    // For 'builder' type, we do nothing since it's disabled
  };
  const handleCloseCreateTask = () => {
    setIsCreateTaskOpen(false);
  };
  const handleCreateFromTemplate = (description, title = 'New Instructions') => {
    setCurrentTask({
      title: title,
      instructions: description,
      isNew: true
    });
    // Show the TaskSetupScreen instead of EditTask
    setIsTaskSetupOpen(true);
    setIsCreateTaskOpen(false);
  };
  const handleCloseEditTask = () => {
    setIsEditTaskOpen(false);
    setCurrentTask(null);
    setIsNewTask(false);
    setTaskAutomations([]); // Clear automations when closing
  };
  const handleSaveTaskSetup = () => {
    // Close TaskSetupScreen and return to Dashboard instead of opening EditTask
    setIsTaskSetupOpen(false);
    setCurrentTask(null);
  };
  const handleCancelTaskSetup = () => {
    setIsTaskSetupOpen(false);
    setCurrentTask(null);
  };
  // Function to add a new automation
  const handleAddAutomation = () => {
    const newAutomation = {
      id: taskAutomations.length + 1,
      name: 'New Automation',
      description: 'Scheduled time → Delegate to AI Custom Agent',
      active: false // Toggled off by default
    };
    setTaskAutomations(prevAutomations => [...prevAutomations, newAutomation]);
  };
  const handleCreateAutomation = () => {
    if (isLimitReached) {
      setIsLimitReachedModalOpen(true);
      return;
    }
    // Store a flag indicating we're creating an automation
    sessionStorage.setItem('pendingAutomationCallback', 'true');
    setIsAutomationBuilderOpen(true);
  };
  const handleCloseAutomationBuilder = () => {
    setIsAutomationBuilderOpen(false);
    // Check if we need to add a new automation
    if (sessionStorage.getItem('pendingAutomationCallback') === 'true') {
      // Clear the flag
      sessionStorage.removeItem('pendingAutomationCallback');
      // Add the new automation
      handleAddAutomation();
    }
  };
  // Toggle usage details modal
  const toggleUsageDetails = () => {
    setIsUsageDetailsOpen(!isUsageDetailsOpen);
  };
  // Toggle warning expanded state
  const toggleWarningExpanded = () => {
    setIsWarningExpanded(!isWarningExpanded);
  };
  // Handle upgrade click
  const handleUpgrade = () => {
    // Close the limit reached modal
    setIsLimitReachedModalOpen(false);
    // Show upgrade confirmation or redirect to upgrade page
    console.log('Upgrade clicked');
  };
  // Handle wait for reset click
  const handleWaitForReset = () => {
    // Close the limit reached modal
    setIsLimitReachedModalOpen(false);
    console.log('Wait for reset clicked');
  };
  if (isRunLogOpen) {
    return <RunLog workflow={selectedWorkflow} onClose={handleCloseRunLog} data-id="element-998" />;
  }
  if (isCreateTaskOpen) {
    return <CreateTask onClose={handleCloseCreateTask} onCreateTask={handleCreateFromTemplate} data-id="element-999" />;
  }
  if (isTaskSetupOpen) {
    return <TaskSetupScreen task={currentTask} onSave={handleSaveTaskSetup} onCancel={handleCancelTaskSetup} isLimitReached={isLimitReached} data-id="element-1000" />;
  }
  if (isAutomationBuilderOpen) {
    return <AutomationBuilder onClose={handleCloseAutomationBuilder} data-id="element-1001" />;
  }
  if (isEditTaskOpen) {
    return <EditTask task={currentTask} onClose={handleCloseEditTask} isNewTask={isNewTask} onCreateAutomation={handleCreateAutomation} automations={taskAutomations} setAutomations={setTaskAutomations} isLimitReached={isLimitReached} data-id="element-1002" />;
  }
  // Usage details modal component
  const UsageDetailsModal = () => <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1003">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-1004">
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1005">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-1006">Usage Details</h1>
          <button onClick={toggleUsageDetails} className="text-gray-500 hover:text-gray-700" data-id="element-1007">
            <X className="w-5 h-5" data-id="element-1008" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1009">
          <div className="flex items-center justify-between mb-4" data-id="element-1010">
            <div data-id="element-1011">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-1012">
                Monthly Usage
              </h2>
              <p className="text-sm text-gray-600" data-id="element-1013">
                Current billing period: Jan 15 - Feb 15
              </p>
            </div>
            <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md" data-id="element-1014">
              <div className="mr-3" data-id="element-1015">
                <div className="text-sm text-gray-600" data-id="element-1016">Used</div>
                <div className="text-xl font-bold text-gray-800" data-id="element-1017">
                  {usageData.used}/{usageData.total}
                </div>
              </div>
              <div className="w-12 h-12 relative" data-id="element-1018">
                <svg className="w-12 h-12" viewBox="0 0 36 36" data-id="element-1019">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#E5E7EB" strokeWidth="3" data-id="element-1020" />
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#4B5563" strokeWidth="3" strokeDasharray={`${usageData.percentage * 0.94} 100`} strokeLinecap="round" transform="rotate(-90 18 18)" data-id="element-1021" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1022">
                  <span className="text-xs font-medium text-gray-700" data-id="element-1023">
                    {usageData.percentage}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-id="element-1024">
            <h3 className="text-sm font-medium text-gray-700 mb-4" data-id="element-1025">
              Usage Trend (Last 30 Days)
            </h3>
            <div className="h-64" data-id="element-1026">
              <UsageChart data-id="element-1027" />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500" data-id="element-1028">
              <div data-id="element-1029">Jan 15</div>
              <div data-id="element-1030">Feb 15</div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1031">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-1032">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-1033">
                Plan Comparison
              </h3>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-1034">
              <div className="grid grid-cols-3 text-sm" data-id="element-1035">
                <div className="p-4 font-medium text-gray-700 bg-gray-50" data-id="element-1036">
                  Features
                </div>
                <div className="p-4 font-medium text-gray-700 bg-gray-50" data-id="element-1037">
                  Free Plan{' '}
                  <span className="text-xs font-normal text-gray-500" data-id="element-1038">
                    (Current)
                  </span>
                </div>
                <div className="p-4 font-medium text-gray-700 bg-gray-50" data-id="element-1039">
                  Premium Plan
                </div>
              </div>
              <div className="grid grid-cols-3 text-sm" data-id="element-1040">
                <div className="p-4 text-gray-600" data-id="element-1041">Monthly Invocations</div>
                <div className="p-4 text-gray-700" data-id="element-1042">5</div>
                <div className="p-4 text-gray-700" data-id="element-1043">50</div>
              </div>
              <div className="grid grid-cols-3 text-sm" data-id="element-1044">
                <div className="p-4 text-gray-600" data-id="element-1045">Custom Instructions</div>
                <div className="p-4 text-gray-700" data-id="element-1046">3</div>
                <div className="p-4 text-gray-700" data-id="element-1047">Unlimited</div>
              </div>
              <div className="grid grid-cols-3 text-sm" data-id="element-1048">
                <div className="p-4 text-gray-600" data-id="element-1049">Agent Memory</div>
                <div className="p-4 text-gray-700" data-id="element-1050">7 days</div>
                <div className="p-4 text-gray-700" data-id="element-1051">90 days</div>
              </div>
              <div className="grid grid-cols-3 text-sm" data-id="element-1052">
                <div className="p-4 text-gray-600" data-id="element-1053">Priority Execution</div>
                <div className="p-4 text-gray-700" data-id="element-1054">
                  <X className="w-4 h-4 text-gray-400" data-id="element-1055" />
                </div>
                <div className="p-4 text-gray-700" data-id="element-1056">
                  <CheckCircle className="w-4 h-4 text-gray-700" data-id="element-1057" />
                </div>
              </div>
              <div className="grid grid-cols-3 text-sm" data-id="element-1058">
                <div className="p-4 text-gray-600" data-id="element-1059">Price</div>
                <div className="p-4 text-gray-700" data-id="element-1060">$0</div>
                <div className="p-4 text-gray-700" data-id="element-1061">$29/month</div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end p-6 border-t border-gray-200" data-id="element-1062">
          <button onClick={toggleUsageDetails} className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium mr-2" data-id="element-1063">
            Close
          </button>
          <button onClick={handleUpgrade} className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium" data-id="element-1064">
            Upgrade to Premium
          </button>
        </div>
      </div>
    </div>;
  return <div className="max-w-full" data-id="element-1065">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-1066">
        <div data-id="element-1067">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-1068">AI Custom Agent</h1>
          <p className="text-gray-600 text-sm" data-id="element-1069">
            Get things done with a proactive AI agent that works autonomously.{' '}
            <span className="text-gray-700 font-medium" data-id="element-1070">Learn more</span>
          </p>
        </div>
        <div className="flex items-center" data-id="element-1071">
          <button className="p-1 rounded-md hover:bg-gray-200" data-id="element-1072">
            <MoreHorizontal className="w-5 h-5 text-gray-500" data-id="element-1073" />
          </button>
          {/* Always show and enable the Create Workflow button */}
          <button className="ml-2 px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium flex items-center" onClick={handleCreateTask} data-id="element-1074">
            Create Workflow
          </button>
        </div>
      </div>
      {/* Limit Reached Warning Banner */}
      {isLimitReached && <div className="bg-gray-100 border border-gray-300 rounded-lg mb-6 overflow-hidden" data-id="element-1075">
          <div className="p-4 flex items-start justify-between" data-id="element-1076">
            <div className="flex items-start" data-id="element-1077">
              <div className="p-1 rounded-full bg-gray-200 mr-3 mt-0.5" data-id="element-1078">
                <AlertCircle className="w-5 h-5 text-gray-700" data-id="element-1079" />
              </div>
              <div data-id="element-1080">
                <h3 className="text-sm font-medium text-gray-800" data-id="element-1081">
                  Monthly limit reached. Resets {usageData.resetDate} (
                  {usageData.daysLeft} days).
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-1082">
                  You've used all {usageData.total} invocations included in your
                  Free plan.
                </p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-1083">
              <button className="text-sm text-gray-700 font-medium mr-3 flex items-center" onClick={toggleWarningExpanded} data-id="element-1084">
                View Details{' '}
                {isWarningExpanded ? <ChevronUp className="ml-1 w-4 h-4" data-id="element-1085" /> : <ChevronDown className="ml-1 w-4 h-4" data-id="element-1086" />}
              </button>
              <button className="px-3 py-1.5 bg-gray-800 text-white rounded-md text-sm font-medium" onClick={handleUpgrade} data-id="element-1087">
                Upgrade Plan
              </button>
            </div>
          </div>
          {/* Expanded warning content */}
          {isWarningExpanded && <div className="px-4 pb-4 border-t border-gray-200 pt-4 bg-gray-50" data-id="element-1088">
              <div className="grid grid-cols-2 gap-6" data-id="element-1089">
                <div data-id="element-1090">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-1091">
                    Current Plan
                  </h4>
                  <div className="bg-white border border-gray-200 rounded-md p-3" data-id="element-1092">
                    <div className="flex justify-between items-center mb-2" data-id="element-1093">
                      <h5 className="text-sm font-medium text-gray-800" data-id="element-1094">
                        Free Plan
                      </h5>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full" data-id="element-1095">
                        Current
                      </span>
                    </div>
                    <ul className="space-y-2 text-sm" data-id="element-1096">
                      <li className="flex items-center text-gray-600" data-id="element-1097">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1098" />
                        <span data-id="element-1099">5 invocations per month</span>
                      </li>
                      <li className="flex items-center text-gray-600" data-id="element-1100">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1101" />
                        <span data-id="element-1102">3 custom instructions</span>
                      </li>
                      <li className="flex items-center text-gray-600" data-id="element-1103">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1104" />
                        <span data-id="element-1105">7 day agent memory</span>
                      </li>
                      <li className="flex items-center text-gray-600" data-id="element-1106">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1107" />
                        <span data-id="element-1108">$0/month</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div data-id="element-1109">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-1110">
                    Recommended Upgrade
                  </h4>
                  <div className="bg-white border border-gray-300 rounded-md p-3" data-id="element-1111">
                    <div className="flex justify-between items-center mb-2" data-id="element-1112">
                      <h5 className="text-sm font-medium text-gray-800" data-id="element-1113">
                        Premium Plan
                      </h5>
                      <span className="text-xs bg-gray-800 text-white px-2 py-0.5 rounded-full" data-id="element-1114">
                        Best Value
                      </span>
                    </div>
                    <ul className="space-y-2 text-sm" data-id="element-1115">
                      <li className="flex items-center text-gray-600" data-id="element-1116">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1117" />
                        <span className="font-medium" data-id="element-1118">
                          50 invocations per month
                        </span>
                      </li>
                      <li className="flex items-center text-gray-600" data-id="element-1119">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1120" />
                        <span data-id="element-1121">Unlimited custom instructions</span>
                      </li>
                      <li className="flex items-center text-gray-600" data-id="element-1122">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1123" />
                        <span data-id="element-1124">90 day agent memory</span>
                      </li>
                      <li className="flex items-center text-gray-600" data-id="element-1125">
                        <CheckCircle className="w-4 h-4 text-gray-700 mr-2 flex-shrink-0" data-id="element-1126" />
                        <span data-id="element-1127">$29/month</span>
                      </li>
                    </ul>
                    <button className="w-full mt-3 px-3 py-1.5 bg-gray-800 text-white rounded-md text-sm font-medium" onClick={handleUpgrade} data-id="element-1128">
                      Upgrade Now
                    </button>
                  </div>
                </div>
              </div>
              <button className="mt-4 text-sm text-gray-700 flex items-center" onClick={toggleUsageDetails} data-id="element-1129">
                View detailed usage <ExternalLink className="ml-1 w-3 h-3" data-id="element-1130" />
              </button>
            </div>}
        </div>}
      {/* Agent and Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1131">
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-1132">
          <h2 className="text-lg font-medium text-gray-800 p-5 border-b border-gray-200" data-id="element-1133">
            Agent
          </h2>
          <div className="flex items-center p-5 justify-between" data-id="element-1134">
            <div className="flex items-center" data-id="element-1135">
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mr-4" data-id="element-1136">
                <span className="text-gray-700 font-medium text-xl" data-id="element-1137">A</span>
              </div>
              <div data-id="element-1138">
                <div className="text-xl font-medium text-gray-800 mb-1" data-id="element-1139">
                  Albert
                </div>
                <button className="flex items-center text-gray-600 hover:text-gray-800 px-3 py-1 border border-gray-200 rounded-md text-sm" data-id="element-1140">
                  <MessageCircle className="w-4 h-4 mr-2" data-id="element-1141" />
                  Chat
                </button>
              </div>
            </div>
            <div className="border-l border-gray-200 pl-6" data-id="element-1142">
              <div className="flex items-center" data-id="element-1143">
                <div className="text-4xl font-bold text-gray-800 mr-3" data-id="element-1144">18</div>
                <div data-id="element-1145">
                  <div className="text-sm text-gray-600" data-id="element-1146">Approvals pending</div>
                  <div className="text-sm text-gray-700 font-medium" data-id="element-1147">
                    Review
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-1148">
          <h2 className="text-lg font-medium text-gray-800 p-5 border-b border-gray-200" data-id="element-1149">
            Performance
          </h2>
          <div className="flex items-center justify-between p-5" data-id="element-1150">
            <div data-id="element-1151">
              <div className="text-3xl font-bold text-gray-800" data-id="element-1152">32</div>
              <div className="text-sm text-gray-600" data-id="element-1153">
                Invocations in last 7 days
              </div>
            </div>
            <div data-id="element-1154">
              <div className="text-3xl font-bold text-gray-800" data-id="element-1155">22%</div>
              <div className="text-sm text-gray-600" data-id="element-1156">Completed</div>
            </div>
            <div className="flex items-center gap-6" data-id="element-1157">
              <div className="flex flex-col items-center" data-id="element-1158">
                <div className="w-16 h-16 relative mb-1" data-id="element-1159">
                  <svg className="w-16 h-16" viewBox="0 0 36 36" data-id="element-1160">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#E5E7EB" strokeWidth="3" data-id="element-1161" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#4B5563" strokeWidth="3" strokeDasharray={`${usageData.percentage * 0.94} 100`} strokeLinecap="round" transform="rotate(-90 18 18)" data-id="element-1162" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col" data-id="element-1163">
                    <span className="text-sm font-bold text-gray-800" data-id="element-1164">
                      {usageData.used}/{usageData.total}
                    </span>
                    <span className="text-xs text-gray-500" data-id="element-1165">Used</span>
                  </div>
                </div>
                <div className="text-xs text-center text-gray-500" data-id="element-1166">
                  Resets {usageData.resetDate}
                </div>
              </div>
              <Link to="/activity" data-id="element-1167">
                <button className="px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1168">
                  View Activity
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Agent Workflows Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1169">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-1170">
          <h2 className="text-lg font-medium text-gray-800" data-id="element-1171">Agent Workflows</h2>
        </div>
        <div className="overflow-x-auto" data-id="element-1172">
          <table className="w-full" data-id="element-1173">
            <thead data-id="element-1174">
              <tr className="bg-gray-50 text-left" data-id="element-1175">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-3/12" data-id="element-1176">
                  Name
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12" data-id="element-1177">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12" data-id="element-1178">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-3/12" data-id="element-1179">
                  Trigger
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-3/12" data-id="element-1180">
                  Last Run
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 text-right" data-id="element-1181">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-1182">
              <WorkflowRow name="Weekly Feedback Analysis and Summary" status="ACTIVE" trigger="Scheduled time" triggerIcon={<Clock className="w-4 h-4 text-white" data-id="element-1184" />} triggerBgColor="bg-gray-800" lastRun="Aug 27, 2025, 8:00:16 AM" type="Basic" onEdit={() => handleEditAutomation({
              name: 'Weekly Feedback Analysis and Summary',
              type: 'Basic'
            })} onViewRunLog={() => handleViewRunLog({
              name: 'Weekly Feedback Analysis and Summary'
            })} isLimitReached={isLimitReached} data-id="element-1183" />
              <WorkflowRow name="Respond to Negative Reviews with Coupons" status="ACTIVE" trigger="Option chosen on a form" triggerIcon={<FileText className="w-4 h-4 text-white" data-id="element-1186" />} triggerBgColor="bg-gray-700" lastRun="Aug 14, 2025, 9:15:37 AM" type="Builder" onEdit={() => handleEditAutomation({
              name: 'Respond to Negative Reviews with Coupons',
              type: 'Builder'
            })} onViewRunLog={() => handleViewRunLog({
              name: 'Respond to Negative Reviews with Coupons'
            })} isLimitReached={isLimitReached} data-id="element-1185" />
              <WorkflowRow name="Inventory Restock Management" status="ACTIVE" trigger="Order placed" triggerIcon={<Package className="w-4 h-4 text-white" data-id="element-1188" />} triggerBgColor="bg-gray-700" lastRun="Aug 29, 2025, 1:23:30 PM" type="Basic" onEdit={() => handleEditAutomation({
              name: 'Inventory Restock Management',
              type: 'Basic'
            })} onViewRunLog={() => handleViewRunLog({
              name: 'Inventory Restock Management'
            })} isLimitReached={isLimitReached} data-id="element-1187" />
              <WorkflowRow name="Low Rating Review Response" status="INACTIVE" trigger="Velo code trigger" triggerIcon={<Code className="w-4 h-4 text-white" data-id="element-1190" />} triggerBgColor="bg-gray-800" lastRun="—" type="Builder" onEdit={() => handleEditAutomation({
              name: 'Low Rating Review Response',
              type: 'Builder'
            })} onViewRunLog={() => handleViewRunLog({
              name: 'Low Rating Review Response'
            })} isLimitReached={isLimitReached} data-id="element-1189" />
              <WorkflowRow name="Negative Review Response with Coupon" status="INACTIVE" trigger="Session booked" triggerIcon={<Calendar className="w-4 h-4 text-white" data-id="element-1192" />} triggerBgColor="bg-gray-700" lastRun="Jul 11, 2025, 9:29:18 AM" type="Basic" onEdit={() => handleEditAutomation({
              name: 'Negative Review Response with Coupon',
              type: 'Basic'
            })} onViewRunLog={() => handleViewRunLog({
              name: 'Negative Review Response with Coupon'
            })} isLimitReached={isLimitReached} data-id="element-1191" />
              <WorkflowRow name="Monthly Sales Performance Report" status="DRAFT" trigger="Scheduled time" triggerIcon={<Clock className="w-4 h-4 text-white" data-id="element-1194" />} triggerBgColor="bg-gray-800" lastRun="Aug 01, 2025, 12:00:00 AM" type="Builder" onEdit={() => handleEditAutomation({
              name: 'Monthly Sales Performance Report',
              type: 'Builder'
            })} onViewRunLog={() => handleViewRunLog({
              name: 'Monthly Sales Performance Report'
            })} isLimitReached={isLimitReached} data-id="element-1193" />
            </tbody>
          </table>
        </div>
      </div>
      {/* Workflow Type Selection Modal */}
      {isWorkflowTypeModalOpen && <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50" data-id="element-1195">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6" data-id="element-1196">
            <div className="flex items-center justify-between mb-6" data-id="element-1197">
              <h1 className="text-2xl font-bold text-gray-800" data-id="element-1198">
                Create Workflow
              </h1>
              <button onClick={handleCloseWorkflowTypeModal} className="text-gray-500 hover:text-gray-700" data-id="element-1199">
                <X className="w-5 h-5" data-id="element-1200" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1201">
              {/* Basic Workflow Option */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:bg-gray-50 flex flex-col h-full" onClick={() => handleSelectWorkflowType('basic')} data-id="element-1202">
                <div className="flex items-center mb-4" data-id="element-1203">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-4" data-id="element-1204">
                    <Zap className="w-5 h-5 text-gray-700" data-id="element-1205" />
                  </div>
                  <h2 className="text-lg font-medium text-gray-800" data-id="element-1206">Basic</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-1207">
                  Quickly create a task for the AI agent to perform. Perfect for
                  single-purpose workflows like inventory management, customer
                  support, or content generation.
                </p>
                <div className="flex justify-end mt-auto" data-id="element-1208">
                  <button className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium" data-id="element-1209">
                    Select
                  </button>
                </div>
              </div>
              {/* Builder Workflow Option (Disabled) */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-full" data-id="element-1210">
                <div className="flex items-center mb-4" data-id="element-1211">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-4" data-id="element-1212">
                    <Layers className="w-5 h-5 text-gray-700" data-id="element-1213" />
                  </div>
                  <h2 className="text-lg font-medium text-gray-800" data-id="element-1214">Builder</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-1215">
                  Create complex, multi-step workflows with branching logic and
                  conditional actions. Perfect for advanced automation needs
                  that require sophisticated decision making.
                </p>
                <div className="flex justify-end mt-auto" data-id="element-1216">
                  <button className="px-4 py-2 bg-gray-300 text-gray-500 rounded-md text-sm font-medium cursor-not-allowed" disabled data-id="element-1217">
                    Select
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200" data-id="element-1218">
              <button onClick={handleCloseWorkflowTypeModal} className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium" data-id="element-1219">
                Cancel
              </button>
            </div>
          </div>
        </div>}
      {/* Usage Details Modal */}
      {isUsageDetailsOpen && <UsageDetailsModal data-id="element-1220" />}
      {/* Limit Reached Modal */}
      <LimitReachedModal isOpen={isLimitReachedModalOpen} onClose={() => setIsLimitReachedModalOpen(false)} onUpgrade={handleUpgrade} onWaitForReset={handleWaitForReset} resetDate={usageData.resetDate} daysLeft={usageData.daysLeft} data-id="element-1221" />
    </div>;
};
const WorkflowRow = ({
  name,
  status,
  trigger,
  triggerIcon,
  triggerBgColor,
  lastRun,
  type = 'Basic',
  onEdit,
  onViewRunLog,
  isLimitReached = false
}) => {
  return <tr className="hover:bg-gray-50" data-id="element-1222">
      <td className="px-6 py-4 text-sm text-gray-800" data-id="element-1223">{name}</td>
      <td className="px-6 py-4 text-sm" data-id="element-1224">
        <TypeBadge type={type} data-id="element-1225" />
      </td>
      <td className="px-6 py-4 text-sm" data-id="element-1226">
        <StatusBadge status={status} data-id="element-1227" />
      </td>
      <td className="px-6 py-4 text-sm" data-id="element-1228">
        <div className="flex items-center" data-id="element-1229">
          <div className={`w-6 h-6 ${triggerBgColor} rounded flex items-center justify-center mr-2`} data-id="element-1230">
            {triggerIcon}
          </div>
          <span className="text-gray-700" data-id="element-1231">{trigger}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1232">{lastRun}</td>
      <td className="px-6 py-4 text-right relative" data-id="element-1233">
        <TableRowMenu trigger={<button className="text-gray-500 hover:text-gray-700" data-id="element-1235">
              <MoreHorizontal className="w-5 h-5" data-id="element-1236" />
            </button>} data-id="element-1234">
          <div className="p-3 flex items-center border-b border-gray-200" data-id="element-1237">
            <div className="w-10 h-5 bg-gray-800 rounded-full relative mr-3" data-id="element-1238">
              <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-1239"></div>
            </div>
            <span className="text-sm font-medium text-gray-700" data-id="element-1240">Active</span>
          </div>
          <TableRowMenu.Item label="Edit" icon={<Edit className="w-4 h-4" data-id="element-1242" />} onClick={onEdit} data-id="element-1241" />
          <TableRowMenu.Item label="Duplicate" icon={<Copy className="w-4 h-4" data-id="element-1244" />} data-id="element-1243" />
          <TableRowMenu.Item label="Rename" icon={<FilePenLine className="w-4 h-4" data-id="element-1246" />} data-id="element-1245" />
          <TableRowMenu.Item label="View run log" icon={<ClipboardList className="w-4 h-4" data-id="element-1248" />} onClick={onViewRunLog} data-id="element-1247" />
          <TableRowMenu.Item label="Delete" icon={<Trash2 className="w-4 h-4" data-id="element-1250" />} data-id="element-1249" />
        </TableRowMenu>
      </td>
    </tr>;
};
// Add TypeBadge component
const TypeBadge = ({
  type
}) => {
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-700';
  let borderColor = 'border-gray-200';
  let icon = <Zap className="w-3 h-3 mr-1 text-gray-700" data-id="element-1251" />;
  if (type === 'Builder') {
    icon = <Layers className="w-3 h-3 mr-1 text-gray-700" data-id="element-1252" />;
  }
  return <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor} border ${borderColor}`} data-id="element-1253">
      {icon} {type}
    </span>;
};
const StatusBadge = ({
  status
}) => {
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-700';
  let borderColor = 'border-gray-200';
  if (status === 'ACTIVE') {
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-700';
    borderColor = 'border-gray-200';
  } else if (status === 'DRAFT') {
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-500';
    borderColor = 'border-gray-200';
  } else if (status === 'INACTIVE') {
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-400';
    borderColor = 'border-gray-200';
  }
  return <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor} border ${borderColor}`} data-id="element-1254">
      {status}
    </span>;
};